import { observer } from 'mobx-react-lite'
import Editor, {
  BtnBold,
  BtnBulletList,
  BtnItalic,
  BtnLink,
  BtnNumberedList,
  BtnRedo,
  BtnUnderline,
  BtnUndo,
  type ContentEditableEvent,
  Separator,
  Toolbar,
  createDropdown,
} from 'react-simple-wysiwyg'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import {
  Alert,
  ContainerHeader,
  Divider,
  EnumHelperText,
  HelperText,
  IconButton,
  SpinnerLoader,
  type IButtonProps,
  type ActionItem,
} from 'shared/ui'
import { TextField } from 'shared/ui/TextField'
import { makeTextFieldAutoWidth } from 'shared/ui/TextField/ActionsMain/TextFieldAutoWidth'
import { KnowledgeBaseDocumentStatusLabel } from 'widgets/KnowledgeBase'
import { useKnowledgeBaseViewContext } from 'pages/settings/pages/knowledgeBase/context/knowledgeBaseViewContext'
import { useDocumentBlocker } from 'pages/settings/pages/knowledgeBase/hooks/useDocumentBlocker'
import styles from './styles.module.scss'

export const KnowledgeBaseDocumentView = observer(() => {
  const { id: paramsId } = useParams()
  const {
    documentsStore: { createDocumentStore },
    pageLayoutStore,
  } = useKnowledgeBaseViewContext()
  const { titleIconProps, headerMenuTooltipIconProps, headerMenuIconAction } = pageLayoutStore || {}
  const {
    title,
    setTitle,
    html,
    setHtml,
    onHandleSave,
    isDisabledSave,
    id,
    onClosePage,
    isLoading,
    errorMessages,
    documentBlockerStore,
    onReset,
    onEditDocument,
    onCreateDocument,
    documentStatus,
    isSavedDraftState,
  } = createDocumentStore
  const [isLocked, setIsLocked] = useState(false)

  useDocumentBlocker(documentBlockerStore)

  useEffect(() => {
    if (paramsId) {
      const fetchDocument = async () => {
        const result = await onEditDocument(Number(paramsId))
        setIsLocked(result?.isLocked ?? false)
      }
      fetchDocument()
    } else {
      onCreateDocument()
    }
  }, [])

  useEffect(() => {
    return () => {
      setIsLocked(false)
      onReset()
    }
  }, [])

  const BtnStyles = createDropdown('Text style', [
    ['Regular text', 'formatBlock', 'DIV'],
    ['Heading 1', 'formatBlock', 'H1'],
    ['Heading 2', 'formatBlock', 'H2'],
    ['Heading 3', 'formatBlock', 'H3'],
  ])

  const onChange = (e: ContentEditableEvent) => {
    setHtml(e.target.value)
  }

  const onAction = async (isDraft: boolean) => {
    await onHandleSave(isDraft)
  }

  if (isLoading) {
    return <SpinnerLoader ariaLabel={'KnowledgeBaseDocumentView'} />
  }

  if (isLocked) {
    return null
  }

  const saveAsDraftButton: ActionItem | null =
    id && !isSavedDraftState
      ? null
      : {
          text: 'Save as draft',
          medium: true,
          buttonProps: {
            contained: 'secondary',
            disabled: isDisabledSave,
            loading: false,
          },
          onAction: () => onAction(true),
        }

  return (
    <>
      <ContainerHeader
        style={{ height: 80, minHeight: 'unset', padding: '0 20px' }}
        customTitle={
          <div className={styles.titleContainer}>
            <TextField
              className={styles.title}
              variant='outline'
              InputProps={{
                placeholder: 'Title',
              }}
              onChange={setTitle}
              value={title}
              mainActions={[makeTextFieldAutoWidth({ minWidth: 150, maxWidth: 350 })]}
              error={errorMessages.title}
            />
            {documentStatus && (
              <KnowledgeBaseDocumentStatusLabel
                status={documentStatus}
                className={styles.documentStatus}
              />
            )}
            {errorMessages.title && (
              <HelperText
                paddingBlock={'3.5px'}
                icon={'alertTriangleSmall'}
                variant={EnumHelperText.Error}
                text={errorMessages.title}
              />
            )}
          </div>
        }
        titleVariant={'heading-md'}
        titleIconProps={titleIconProps}
        actionIconTooltipProps={headerMenuTooltipIconProps}
        titleIconAction={headerMenuIconAction}
        isBorderBottom
        isBorderRight
        isTitleDivider
        actionsGap={8}
        actions={[
          ...(saveAsDraftButton ? [saveAsDraftButton] : []),
          {
            text: id && !isSavedDraftState ? 'Save changes' : 'Publish',
            medium: true,
            buttonProps: {
              contained: 'primary' as IButtonProps['contained'],
              disabled: isDisabledSave,
              loading: false,
            },
            onAction: () => onAction(false),
          },
          {
            iconButtonComponent: (
              <div className={styles.closeButtonWrapper}>
                <Divider
                  orientation='vertical'
                  sx={{ height: '16px !important', marginRight: '8px' }}
                />
                <IconButton
                  className={styles.close}
                  ariaLabel='close_setting'
                  icon='close'
                  variant='contained'
                  color='tertiary'
                  size='medium'
                  onClick={onClosePage}
                />
              </div>
            ),
          },
        ]}
      />
      {errorMessages.contents && (
        <Alert
          item={{
            type: 'error',
            desc: errorMessages.contents,
          }}
          className={styles.alert}
        />
      )}
      <div className={styles.editorWrapper}>
        <Editor
          value={html}
          onChange={onChange}
          className={styles.editor}
          placeholder='Type anything your AI should know — answers, instructions, or notes'
        >
          <Toolbar>
            <BtnUndo />
            <BtnRedo />
            <Separator />
            <BtnStyles />
            <BtnBold />
            <BtnItalic />
            <BtnUnderline />
            <Separator />
            <BtnNumberedList />
            <BtnBulletList />
            <Separator />
            <BtnLink />
          </Toolbar>
        </Editor>
      </div>
    </>
  )
})
